import React, { useState, useMemo } from 'react'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts'
import {
  ChartBarIcon,
  PresentationChartLineIcon,
  ChartPieIcon,
  CalendarIcon
} from '@heroicons/react/24/outline'
import { format, parseISO, differenceInDays, startOfMonth, endOfMonth, isSameMonth } from 'date-fns'

export type ChartType = 'line' | 'bar' | 'area'
export type AggregationType = 'daily' | 'monthly'

interface TrendChartProps {
  data: Array<{
    [key: string]: any
  }>
  dataKey: string
  xAxisKey: string
  title?: string
  color?: string
  height?: number
  tickFormatter?: (value: any) => string
  valueFormatter?: (value: number) => string
  labelFormatter?: (value: any) => string
  enableToggle?: boolean
  defaultChartType?: ChartType
  showGrid?: boolean
  showTooltip?: boolean
  fillOpacity?: number
  strokeWidth?: number
  enableGradient?: boolean
  gradientId?: string
  dateRange?: {
    start: Date
    end: Date
  }
}

export function TrendChart({
  data,
  dataKey,
  xAxisKey,
  title,
  color = '#2563eb',
  height = 300,
  tickFormatter,
  valueFormatter = (value: number) =>
    `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  labelFormatter,
  enableToggle = true,
  defaultChartType = 'line',
  showGrid = true,
  showTooltip = true,
  fillOpacity = 0.25,
  strokeWidth = 2,
  enableGradient = true,
  gradientId = 'colorGradient',
  dateRange
}: TrendChartProps) {
  const [chartType, setChartType] = useState<ChartType>(defaultChartType)
  const [selectedMonth, setSelectedMonth] = useState<string>('')

  // Calculate aggregation type and available months
  const chartAnalysis = useMemo(() => {
    if (!data.length || !dateRange) {
      return {
        aggregationType: 'daily' as AggregationType,
        availableMonths: [],
        shouldShowMonthSelector: false,
        chartData: data,
        daysDifference: 0
      }
    }

    const daysDifference = differenceInDays(dateRange.end, dateRange.start)
    const shouldUseMonthly = daysDifference > 31

    if (!shouldUseMonthly) {
      return {
        aggregationType: 'daily' as AggregationType,
        availableMonths: [],
        shouldShowMonthSelector: false,
        chartData: data,
        daysDifference
      }
    }

    // Get available months from the data
    const monthsSet = new Set<string>()
    data.forEach((item) => {
      if (item[xAxisKey]) {
        try {
          const date = parseISO(item[xAxisKey])
          const monthKey = format(date, 'yyyy-MM')
          monthsSet.add(monthKey)
        } catch (error) {
          console.warn('Failed to parse date:', item[xAxisKey])
        }
      }
    })

    const availableMonths = Array.from(monthsSet)
      .sort()
      .map((monthKey) => ({
        key: monthKey,
        label: format(parseISO(monthKey + '-01'), 'MMMM yyyy')
      }))

    // Set default selected month if not set
    const currentSelectedMonth =
      selectedMonth || (availableMonths.length > 0 ? availableMonths[0]?.key : '')

    // Create monthly aggregated data
    const monthlyData = availableMonths.map((month) => {
      const monthStart = startOfMonth(parseISO(month.key + '-01'))
      const monthEnd = endOfMonth(monthStart)

      const monthlyTotal = data
        .filter((item) => {
          try {
            const itemDate = parseISO(item[xAxisKey])
            return itemDate >= monthStart && itemDate <= monthEnd
          } catch {
            return false
          }
        })
        .reduce((sum, item) => sum + (item[dataKey] || 0), 0)

      return {
        [xAxisKey]: month.key + '-01',
        [dataKey]: monthlyTotal,
        monthLabel: month.label
      }
    })

    // Create daily data for selected month (if showing daily)
    const dailyDataForMonth = currentSelectedMonth
      ? data.filter((item) => {
          try {
            const itemDate = parseISO(item[xAxisKey])
            const selectedDate = parseISO(currentSelectedMonth + '-01')
            return isSameMonth(itemDate, selectedDate)
          } catch {
            return false
          }
        })
      : []

    return {
      aggregationType: 'monthly' as AggregationType,
      availableMonths,
      shouldShowMonthSelector: true,
      chartData: monthlyData,
      dailyDataForMonth,
      daysDifference,
      selectedMonth: currentSelectedMonth
    }
  }, [data, dateRange, xAxisKey, dataKey, selectedMonth])

  // State for showing daily vs monthly view
  const [viewMode, setViewMode] = useState<'monthly' | 'daily'>('monthly')

  // Initialize selected month from chart analysis
  React.useEffect(() => {
    if (
      chartAnalysis.aggregationType === 'monthly' &&
      chartAnalysis.selectedMonth &&
      !selectedMonth
    ) {
      setSelectedMonth(chartAnalysis.selectedMonth)
    }
  }, [chartAnalysis.aggregationType, chartAnalysis.selectedMonth, selectedMonth])

  // Determine what data to show
  const displayData = useMemo(() => {
    if (chartAnalysis.aggregationType === 'daily') {
      return chartAnalysis.chartData
    }

    if (viewMode === 'monthly') {
      return chartAnalysis.chartData
    } else {
      return chartAnalysis.dailyDataForMonth || []
    }
  }, [chartAnalysis, viewMode])

  const chartMargins = {
    top: 15,
    right: 20,
    left: 50,
    bottom: chartAnalysis.aggregationType === 'monthly' && viewMode === 'monthly' ? 35 : 65
  }

  // Dynamic tick formatter based on view mode
  const dynamicTickFormatter = useMemo(() => {
    if (tickFormatter) return tickFormatter

    if (chartAnalysis.aggregationType === 'monthly' && viewMode === 'monthly') {
      return (value: string) => {
        try {
          const date = parseISO(value)
          // Check if data spans multiple years
          const hasMultipleYears = chartAnalysis.availableMonths.some((month) => {
            const monthDate = parseISO(month.key + '-01')
            return monthDate.getFullYear() !== date.getFullYear()
          })

          // Show year only if data spans multiple years
          return format(date, hasMultipleYears ? 'MMM yy' : 'MMM')
        } catch {
          return value
        }
      }
    }

    return (value: string) => {
      try {
        const date = parseISO(value)
        return format(date, 'MMM dd')
      } catch {
        return value
      }
    }
  }, [tickFormatter, chartAnalysis.aggregationType, chartAnalysis.availableMonths, viewMode])

  const isMonthlyView = chartAnalysis.aggregationType === 'monthly' && viewMode === 'monthly'

  const commonXAxisProps = {
    dataKey: xAxisKey,
    angle: isMonthlyView ? 0 : -45,
    textAnchor: isMonthlyView ? ('middle' as const) : ('end' as const),
    height: isMonthlyView ? 35 : 65,
    tickFormatter: dynamicTickFormatter,
    interval: 0
  }

  const commonYAxisProps = {
    tickFormatter: valueFormatter,
    width: 80
  }

  const tooltipProps = showTooltip
    ? {
        labelFormatter:
          labelFormatter ||
          ((value: string) => {
            try {
              const date = parseISO(value)
              if (isMonthlyView) {
                // For monthly view, show just the month and year
                const hasMultipleYears = chartAnalysis.availableMonths.some((month) => {
                  const monthDate = parseISO(month.key + '-01')
                  return monthDate.getFullYear() !== date.getFullYear()
                })
                return format(date, hasMultipleYears ? 'MMMM yyyy' : 'MMMM')
              } else {
                // For daily view, show full date
                return format(date, 'MMM dd, yyyy')
              }
            } catch {
              return value
            }
          }),
        formatter: (value: number) => {
          const label = isMonthlyView ? 'Monthly Total' : 'Daily Cost'
          return [valueFormatter(value), label]
        }
      }
    : undefined

  const renderChart = () => {
    const chartProps = {
      data: displayData,
      margin: chartMargins
    }

    switch (chartType) {
      case 'line':
        return (
          <LineChart {...chartProps}>
            {enableGradient && (
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={color} stopOpacity={0.1} />
                </linearGradient>
              </defs>
            )}
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />}
            <XAxis {...commonXAxisProps} />
            <YAxis {...commonYAxisProps} />
            {showTooltip && <Tooltip {...tooltipProps} />}
            <Line
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={strokeWidth}
              dot={{ fill: color, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 0 }}
              fill={enableGradient ? `url(#${gradientId})` : color}
            />
          </LineChart>
        )

      case 'area':
        return (
          <AreaChart {...chartProps}>
            {enableGradient && (
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={color} stopOpacity={0.1} />
                </linearGradient>
              </defs>
            )}
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />}
            <XAxis {...commonXAxisProps} />
            <YAxis {...commonYAxisProps} />
            {showTooltip && <Tooltip {...tooltipProps} />}
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={strokeWidth}
              fill={enableGradient ? `url(#${gradientId})` : color}
              fillOpacity={fillOpacity}
            />
          </AreaChart>
        )

      case 'bar':
        return (
          <BarChart {...chartProps}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />}
            <XAxis {...commonXAxisProps} />
            <YAxis {...commonYAxisProps} />
            {showTooltip && <Tooltip {...tooltipProps} />}
            <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
          </BarChart>
        )

      default:
        // Fallback to line chart
        return (
          <LineChart {...chartProps}>
            {enableGradient && (
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={color} stopOpacity={0.1} />
                </linearGradient>
              </defs>
            )}
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />}
            <XAxis {...commonXAxisProps} />
            <YAxis {...commonYAxisProps} />
            {showTooltip && <Tooltip {...tooltipProps} />}
            <Line
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={strokeWidth}
              dot={{ fill: color, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 0 }}
              fill={enableGradient ? `url(#${gradientId})` : color}
            />
          </LineChart>
        )
    }
  }

  const getIcon = (type: ChartType) => {
    switch (type) {
      case 'line':
        return <PresentationChartLineIcon className="h-4 w-4" />
      case 'area':
        return <ChartPieIcon className="h-4 w-4" />
      case 'bar':
        return <ChartBarIcon className="h-4 w-4" />
    }
  }

  const getButtonClass = (type: ChartType) => {
    const baseClass =
      'inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200'
    return chartType === type
      ? `${baseClass} bg-blue-100 text-blue-700 border border-blue-200`
      : `${baseClass} bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100`
  }

  return (
    <div className="w-full">
      {enableToggle && (
        <div className="flex items-center justify-between mb-4">
          {title && <h3 className="text-lg font-semibold text-gray-900">{title}</h3>}
          <div className="flex items-center gap-2">
            {/* Month/Daily View Toggle for multi-month ranges */}
            {chartAnalysis.shouldShowMonthSelector && (
              <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-gray-200 mr-2">
                <button
                  onClick={() => setViewMode('monthly')}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200 ${
                    viewMode === 'monthly'
                      ? 'bg-green-100 text-green-700 border border-green-200'
                      : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                  }`}
                  title="Monthly View"
                >
                  <CalendarIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Monthly</span>
                </button>
                <button
                  onClick={() => setViewMode('daily')}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200 ${
                    viewMode === 'daily'
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                  }`}
                  title="Daily View"
                >
                  <ChartBarIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Daily</span>
                </button>
              </div>
            )}

            {/* Chart Type Toggle */}
            <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-gray-200">
              <button
                onClick={() => setChartType('line')}
                className={getButtonClass('line')}
                title="Line Chart"
              >
                {getIcon('line')}
                <span className="hidden sm:inline">Line</span>
              </button>
              <button
                onClick={() => setChartType('area')}
                className={getButtonClass('area')}
                title="Area Chart"
              >
                {getIcon('area')}
                <span className="hidden sm:inline">Area</span>
              </button>
              <button
                onClick={() => setChartType('bar')}
                className={getButtonClass('bar')}
                title="Bar Chart"
              >
                {getIcon('bar')}
                <span className="hidden sm:inline">Bar</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Month Selector for Daily View */}
      {chartAnalysis.shouldShowMonthSelector &&
        viewMode === 'daily' &&
        chartAnalysis.availableMonths.length > 0 && (
          <div className="mb-3 flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Select Month for Daily View:
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="text-sm px-3 py-1.5 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-36"
              style={{
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1rem 1rem',
                paddingRight: '2.5rem'
              }}
            >
              {chartAnalysis.availableMonths.map((month) => (
                <option key={month.key} value={month.key}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>
        )}

      {/* Chart Description */}
      {chartAnalysis.shouldShowMonthSelector && (
        <div className="mb-2 text-xs text-gray-500 px-1">
          {viewMode === 'monthly'
            ? `Showing monthly totals for ${chartAnalysis.availableMonths.length} months`
            : `Showing daily data for ${chartAnalysis.availableMonths.find((m) => m.key === selectedMonth)?.label || 'selected month'}`}
        </div>
      )}

      <div className="w-full" style={{ height: height }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  )
}
